import React, {
  createContext,
  useState,
  ReactNode,
  useCallback,
  useEffect,
  useContext,
  useMemo,
} from 'react';
import {
  ClientCompat,
  LandTransporter,
  Case,
  VehicleModel,
  VehicleModelDataSource,
  VehicleType,
  Manufacturer,
  BusinessPartner,
  BusinessPartnerLocation,
  BusinessPartnerDataSource,
  BusinessPartnerType,
  BusinessPartnerCategory,
  BusinessPartnerRole,
  BusinessPartnerStatus,
  CompanyTimeSettings,
  CompanyOCRSettings,
  CompanyDeadlineRuleSettings,
  CompanyPartnerSettings,
  CompanyPartnerSelection,
  CompanyVehicleModelSettings,
  CompanyInfo,
  TransportFeeSettings,
  DeadlineRule,
  CreateDeadlineRuleInput,
  UpdateDeadlineRuleInput,
} from '../types';
import { SupabaseMasterDataService } from '../services/supabaseService';
import { VehicleTypeService } from '../services/vehicleTypeService';
import { SupabaseDeadlineRuleService } from '../services/supabase/supabaseDeadlineRuleService';
import { SupabaseVehicleService } from '../services/supabase/supabaseVehicleService';
import { CacheManager } from '../utils/cacheManager';
import { isDemoMode } from '../utils/environment';
import { fetchDataByTiers } from '../utils/tierBasedDataFetching';
import { AuthContext } from './AuthContext';
import { DEFAULT_TRANSPORT_FEE } from '../constants/company';
import logger from '../utils/logger';

/**
 * Supabaseサービスインスタンス取得関数（キャッシュ管理付き）
 */
function getSupabaseServiceInstance(): SupabaseMasterDataService {
  return CacheManager.getInstance('supabaseService', () => {
    logger.debug('新しいSupabaseMasterDataServiceインスタンスを作成');
    return new SupabaseMasterDataService();
  });
}

export interface MasterDataContextType {
  // データ
  clients: ClientCompat[];
  landTransporters: LandTransporter[];
  cases: Case[];
  vehicleModels: VehicleModel[];
  vehicleTypes: VehicleType[];
  manufacturers: Manufacturer[];
  companyVehicles: CompanyVehicle[]; // 会社車両データ
  businessPartners: BusinessPartner[]; // FL-28
  companies: CompanyInfo[]; // 会社一覧（取引先マスタ管理用）
  companyTimeSettings: CompanyTimeSettings[]; // FL-59
  companyOCRSettings: CompanyOCRSettings[]; // OCR設定
  companyDeadlineRuleSettings: CompanyDeadlineRuleSettings[]; // 締切ルール設定
  companyPartnerSettings: CompanyPartnerSettings[]; // 取引先表示設定
  companyVehicleModelSettings: CompanyVehicleModelSettings[]; // 車両モデル選択設定
  companyTransportFeeSettings: TransportFeeSettings[]; // 陸送費設定
  deadlineRules: DeadlineRule[]; // FL-110: 締切ルール
  loading: boolean;

  // CRUD操作
  addClient: (client: ClientCompat) => Promise<void>;
  updateClient: (id: string, client: Partial<ClientCompat>) => Promise<void>;
  removeClient: (id: string) => Promise<void>;
  addLandTransporter: (transporter: LandTransporter) => Promise<void>;
  updateLandTransporter: (id: string, transporter: Partial<LandTransporter>) => Promise<void>;
  removeLandTransporter: (id: string) => Promise<void>;
  addCase: (case_: Case) => Promise<void>;
  updateCase: (id: string, case_: Partial<Case>) => Promise<void>;
  removeCase: (id: string) => Promise<void>;
  deleteCase: (id: string) => Promise<void>;
  addVehicleModel: (model: VehicleModel) => Promise<void>;
  addVehicleModelWithDetails: (
    modelName: string,
    manufacturerName: string
  ) => Promise<VehicleModel>;
  updateVehicleModel: (id: string, model: Partial<VehicleModel>) => Promise<void>;
  deleteVehicleModel: (id: string) => Promise<void>;
  addManufacturer: (manufacturer: Manufacturer) => Promise<void>;
  updateManufacturer: (id: string, manufacturer: Partial<Manufacturer>) => Promise<void>;
  deleteManufacturer: (id: string) => Promise<void>;
  addVehicleType: (type: Omit<VehicleType, 'id'>) => Promise<VehicleType>;
  updateVehicleType: (id: string, type: Partial<VehicleType>) => Promise<void>;
  deleteVehicleType: (id: string) => Promise<void>;

  // FL-28: BusinessPartner CRUD操作
  addBusinessPartner: (partner: BusinessPartner) => Promise<void>;
  updateBusinessPartner: (id: string, partner: Partial<BusinessPartner>) => Promise<void>;
  removeBusinessPartner: (id: string) => Promise<void>;
  addBusinessPartnerLocation: (
    partnerId: string,
    location: BusinessPartnerLocation
  ) => Promise<void>;
  updateBusinessPartnerLocation: (
    partnerId: string,
    locationId: string,
    location: Partial<BusinessPartnerLocation>
  ) => Promise<void>;
  removeBusinessPartnerLocation: (partnerId: string, locationId: string) => Promise<void>;

  // FL-59: 陸送会社時間設定CRUD操作
  addCompanyTimeSettings: (settings: CompanyTimeSettings) => Promise<void>;
  updateCompanyTimeSettings: (id: string, settings: Partial<CompanyTimeSettings>) => Promise<void>;
  removeCompanyTimeSettings: (id: string) => Promise<void>;
  getCompanyTimeSettingsByCompanyId: (companyId: string) => CompanyTimeSettings | null;

  // 陸送会社OCR設定CRUD操作
  addCompanyOCRSettings: (settings: CompanyOCRSettings) => Promise<void>;
  updateCompanyOCRSettings: (id: string, settings: Partial<CompanyOCRSettings>) => Promise<void>;
  removeCompanyOCRSettings: (id: string) => Promise<void>;
  getCompanyOCRSettingsByCompanyId: (companyId: string) => CompanyOCRSettings | null;
  initializeCompanyOCRSettings: (companyId: string) => Promise<void>;

  // 締切ルール設定CRUD操作
  addCompanyDeadlineRuleSettings: (settings: CompanyDeadlineRuleSettings) => Promise<void>;
  updateCompanyDeadlineRuleSettings: (
    id: string,
    settings: Partial<CompanyDeadlineRuleSettings>
  ) => Promise<void>;
  removeCompanyDeadlineRuleSettings: (id: string) => Promise<void>;
  getCompanyDeadlineRuleSettingsByCompanyId: (
    companyId: string
  ) => CompanyDeadlineRuleSettings | null;
  initializeCompanyDeadlineRuleSettings: (companyId: string) => Promise<void>;

  // 陸送会社取引先表示設定CRUD操作
  addCompanyPartnerSettings: (settings: CompanyPartnerSettings) => Promise<void>;
  updateCompanyPartnerSettings: (
    id: string,
    settings: Partial<CompanyPartnerSettings>
  ) => Promise<void>;
  removeCompanyPartnerSettings: (id: string) => Promise<void>;
  getCompanyPartnerSettingsByCompanyId: (companyId: string) => CompanyPartnerSettings | null;
  initializeCompanyPartnerSettings: (companyId: string) => Promise<void>;

  // 陸送会社車両モデル選択設定CRUD操作
  addCompanyVehicleModelSettings: (settings: CompanyVehicleModelSettings) => Promise<void>;
  updateCompanyVehicleModelSettings: (
    id: string,
    settings: Partial<CompanyVehicleModelSettings>
  ) => Promise<void>;
  removeCompanyVehicleModelSettings: (id: string) => Promise<void>;
  getCompanyVehicleModelSettingsByCompanyId: (
    companyId: string
  ) => CompanyVehicleModelSettings | null;
  initializeCompanyVehicleModelSettings: (companyId: string) => Promise<void>;

  // FL-110: 締切ルールCRUD操作
  addDeadlineRule: (rule: CreateDeadlineRuleInput) => Promise<DeadlineRule>;
  updateDeadlineRule: (id: string, rule: UpdateDeadlineRuleInput) => Promise<DeadlineRule>;
  removeDeadlineRule: (id: string) => Promise<void>;
  getDeadlineRulesByBusinessPartnerId: (
    businessPartnerId: string,
    companyId: string
  ) => DeadlineRule[];
  refreshDeadlineRules: () => Promise<void>;

  // FL-116: 陸送費設定
  getCompanyTransportFeeSettingsByCompanyId: (companyId: string) => TransportFeeSettings | null;
  updateCompanyTransportFeeSettings: (
    companyId: string,
    settings: Partial<TransportFeeSettings>
  ) => Promise<void>;

  // 陸送会社取引先選択設定（詳細）CRUD操作
  addCompanyPartnerSelection: (selection: CompanyPartnerSelection) => Promise<void>;
  updateCompanyPartnerSelection: (
    id: string,
    selection: Partial<CompanyPartnerSelection>
  ) => Promise<void>;
  removeCompanyPartnerSelection: (id: string) => Promise<void>;
  getCompanyPartnerSelectionsByCompanyId: (companyId: string) => CompanyPartnerSelection[];
  getCompanyPartnerSelectionById: (id: string) => CompanyPartnerSelection | null;
  enableCompanyPartnerSelection: (id: string) => Promise<void>;
  disableCompanyPartnerSelection: (id: string) => Promise<void>;
  updateCompanyPartnerUsage: (id: string, usageCount: number, lastUsedAt?: Date) => Promise<void>;

  // ヘルパー関数
  getClientById: (id: string) => ClientCompat | null;
  getLandTransporterById: (id: string) => LandTransporter | null;
  getCaseById: (id: string) => Case | null;
  getVehicleModelById: (id: string) => VehicleModel | undefined;
  getVehicleTypeById: (id: string) => VehicleType | null;
  getManufacturerById: (id: string) => Manufacturer | undefined;
  getCompanyVehicleById: (id: string) => CompanyVehicle | undefined; // 会社車両取得
  getBusinessPartnerById: (id: string) => BusinessPartner | undefined; // FL-28
  getBusinessPartnerLocationById: (
    partnerId: string,
    locationId: string
  ) => BusinessPartnerLocation | undefined; // FL-28

  // 車両モデルフィルタリング機能
  getFilteredVehicleModels: (companyId?: string) => VehicleModel[];

  // ラクハイ管理者専用：すべての車両モデルを取得（会社フィルタリング無視）
  getAllVehicleModelsForAdmin: () => VehicleModel[];
  // ラクハイ管理者専用：すべての車種を取得（会社フィルタリング無視）
  getAllVehicleTypesForAdmin: () => VehicleType[];

  // 車両モデル使用状況カウント機能
  getVehicleModelActiveUsageCount: (vehicleModelId: string) => number;
  getAvailableVehicleModels: (companyId?: string) => VehicleModel[];
  isDomesticModel: (model: VehicleModel) => boolean;

  // その他
  refreshData: () => Promise<void>;
  refreshBusinessPartners: () => Promise<void>; // 取引先データ再取得
  refreshCompanies: () => Promise<void>; // 会社データ再取得
  refreshVehicleTypes: () => Promise<void>; // 車種データ再取得
  refreshLandTransporters: () => Promise<void>; // 陸送員データ再取得
  getAllBusinessPartners: () => Promise<BusinessPartner[]>; // システム設定用：全取引先取得（フィルタなし）
  resetData: () => void;
}

// CompanyVehicle型定義
export interface CompanyVehicle {
  id: string;
  company_id: string;
  plate_number: string;
  name?: string;
  vehicle_type?: string;
  manufacturer?: string;
  model?: string;
  year?: number;
  status?: string;
  specifications?: Record<string, any>;
  equipment?: Record<string, any>;
  restrictions?: Record<string, any>;
  assigned_transporter_id?: string;
  created_at?: string;
  updated_at?: string;
  created_by?: string;
  data_source?: string;
}

// eslint-disable-next-line react-refresh/only-export-components
export const MasterDataContext = createContext<MasterDataContextType | undefined>(undefined);

export const MasterDataProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [clients, setClients] = useState<ClientCompat[]>([]);
  const [landTransporters, setLandTransporters] = useState<LandTransporter[]>([]);
  const [cases, setCases] = useState<Case[]>([]);
  const [vehicleModels, setVehicleModels] = useState<VehicleModel[]>([]);
  const [vehicleTypes, setVehicleTypes] = useState<VehicleType[]>([]);
  const [manufacturers, setManufacturers] = useState<Manufacturer[]>([]);
  const [companyVehicles, setCompanyVehicles] = useState<CompanyVehicle[]>([]);
  const [businessPartners, setBusinessPartners] = useState<BusinessPartner[]>([]);
  const [companies, setCompanies] = useState<CompanyInfo[]>([]);
  const [companyTimeSettings, setCompanyTimeSettings] = useState<CompanyTimeSettings[]>([]);
  const [companyOCRSettings, setCompanyOCRSettings] = useState<CompanyOCRSettings[]>([]);
  const [companyDeadlineRuleSettings, setCompanyDeadlineRuleSettings] = useState<
    CompanyDeadlineRuleSettings[]
  >([]);
  const [companyPartnerSettings, setCompanyPartnerSettings] = useState<CompanyPartnerSettings[]>(
    []
  );
  const [companyVehicleModelSettings, setCompanyVehicleModelSettings] = useState<
    CompanyVehicleModelSettings[]
  >([]);
  const [companyTransportFeeSettings, setCompanyTransportFeeSettings] = useState<
    TransportFeeSettings[]
  >([]);
  const [companyPartnerSelections, setCompanyPartnerSelections] = useState<
    CompanyPartnerSelection[]
  >([]);
  const [deadlineRules, setDeadlineRules] = useState<DeadlineRule[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  // 認証情報取得（循環依存回避のためAuthContextを直接使用）
  const authContext = useContext(AuthContext);
  const user = authContext?.user || null;

  // サービス初期化
  const deadlineRuleService = useMemo(() => new SupabaseDeadlineRuleService(), []);

  // データ読み込み
  const refreshData = useCallback(async () => {
    // 認証されていない場合はスキップ（Supabaseクライアント初期化前のエラーを防ぐ）
    if (!user) {
      return;
    }

    setLoading(true);

    try {
      // 🔍 user.companyId検証: fetchDataByTiers呼び出し前に確認
      if (!user?.companyId) {
        logger.warn('⚠️ MasterDataContext: user.companyIdが未設定です', {
          userId: user?.id,
          message: '会社別データ取得がスキップされます',
        });
      }

      // キャッシュ無効化で新しいサービスインスタンスを強制作成
      CacheManager.invalidate('supabaseService');
      const dataService = getSupabaseServiceInstance();

      // Tier-based data fetching（優先度に基づいた段階的データ取得）
      const tieredData = await fetchDataByTiers(dataService, deadlineRuleService, user?.companyId);

      // ✅ fetchDataByTiers完了後の詳細ログ
      logger.info('MasterDataContext: データ取得完了', {
        tier1Companies: tieredData.tier1.companies.length,
        tier1BusinessPartners: tieredData.tier1.businessPartners.length,
        tier1VehicleModels: tieredData.tier1.vehicleModels.length,
        tier2VehicleTypes: tieredData.tier2.vehicleTypes.length,
        tier2DeadlineRules: tieredData.tier2.deadlineRules.length,
        otherDataLandTransporters: tieredData.otherData.landTransporters.length,
        otherDataCases: tieredData.otherData.cases.length,
        otherDataManufacturers: tieredData.otherData.manufacturers.length,
      });

      // Tier 1 state updates（即座に状態を更新してレンダリングを開始）
      setCompanies(tieredData.tier1.companies.map(c => ({ id: c.id, name: c.name, code: c.code })));
      setBusinessPartners(tieredData.tier1.businessPartners);
      setVehicleModels(tieredData.tier1.vehicleModels);

      // Tier 2 state updates（操作に必要な補足データを設定）
      setVehicleTypes(tieredData.tier2.vehicleTypes);
      setDeadlineRules(tieredData.tier2.deadlineRules);

      // Tier 3 state updates（補足的なデータを設定）
      setCompanyVehicleModelSettings(
        tieredData.tier3.companyVehicleModelSettings
          ? [tieredData.tier3.companyVehicleModelSettings]
          : []
      );
      setCompanyPartnerSelections(tieredData.tier3.companyPartnerSelections);

      // その他のデータを設定
      setClients([]);
      setLandTransporters(tieredData.otherData.landTransporters);
      setCases(tieredData.otherData.cases);
      setManufacturers(tieredData.otherData.manufacturers);
      setCompanyVehicles([]);
      setCompanyPartnerSettings([]);
      setCompanyTransportFeeSettings([]); // 仮データ
    } catch (error) {
      logger.error('❌ データ読み込みエラー:', { error });
    } finally {
      setLoading(false);
    }
  }, [user]); // userが変更されたときに再実行

  // 取引先データのみ再取得
  const refreshBusinessPartners = useCallback(async () => {
    try {
      CacheManager.invalidate('supabaseService');
      const dataService = getSupabaseServiceInstance();
      const businessPartnersData = await dataService.getBusinessPartners();
      setBusinessPartners(Array.isArray(businessPartnersData) ? businessPartnersData : []);
    } catch (error) {
      logger.error('❌ 取引先データ再取得エラー:', { error });
    }
  }, []);

  // 会社データのみ再取得
  const refreshCompanies = useCallback(async () => {
    try {
      CacheManager.invalidate('supabaseService');
      const dataService = getSupabaseServiceInstance();
      const companiesData = await dataService.getCompanies();
      setCompanies(
        Array.isArray(companiesData)
          ? companiesData.map(c => ({ id: c.id, name: c.name, code: c.code }))
          : []
      );
    } catch (error) {
      logger.error('❌ 会社データ再取得エラー:', { error });
    }
  }, []);

  // 車種データのみ再取得
  const refreshVehicleTypes = useCallback(async () => {
    try {
      logger.debug('🔄 車種データ再取得開始...');
      const vehicleTypesData = await VehicleTypeService.getAll();
      setVehicleTypes(Array.isArray(vehicleTypesData) ? vehicleTypesData : []);
      logger.debug('✅ 車種データ再取得完了:', vehicleTypesData.length);
    } catch (error) {
      logger.error('❌ 車種データ再取得エラー:', { error });
    }
  }, []);

  // 陸送員データのみ再取得（CacheManager使用）
  const refreshLandTransporters = useCallback(async () => {
    try {
      logger.debug('🔄 陸送員データ再取得開始（CacheManager使用）');

      // CacheManagerでキャッシュクリア
      CacheManager.invalidate('supabaseService');
      const freshDataService = getSupabaseServiceInstance();

      // 強制的に新しいデータを取得
      const landTransportersData = await freshDataService.getLandTransporters();
      setLandTransporters(Array.isArray(landTransportersData) ? landTransportersData : []);

      logger.debug(`✅ 陸送員データ再取得完了 - ${landTransportersData?.length || 0}件`);
      logger.debug(
        '📋 更新された陸送員データ詳細:',
        landTransportersData?.map(lt => ({
          name: lt.name,
          id: lt.id,
          monday: lt.workingDays?.monday,
          tuesday: lt.workingDays?.tuesday,
          wednesday: lt.workingDays?.wednesday,
          thursday: lt.workingDays?.thursday,
          friday: lt.workingDays?.friday,
        }))
      );

      // 19:00まで稼働できる陸送員をハイライト
      const lateWorkers = landTransportersData?.filter(lt =>
        ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'].some(
          day => lt.workingDays?.[day as keyof typeof lt.workingDays]?.endTime === '19:00'
        )
      );
      logger.debug(
        '🌟 19:00まで稼働可能な陸送員:',
        lateWorkers?.map(lt => lt.name)
      );
    } catch (error) {
      logger.error('❌ 陸送員データ再取得エラー:', { error });
    }
  }, []);

  // データリセット
  const resetData = useCallback(async () => {
    logger.debug('🔄 データリセット開始...');
    setLoading(true);

    try {
      // デモモード判定してリセット方式を選択
      if (isDemoMode()) {
        logger.debug('🎭 デモモード: DemoResetServiceを使用したスナップショット復元');
        try {
          // 新しいDemoResetServiceを使用
          const { demoResetService } = await import('../services/demoResetService');

          // 現在のユーザーの会社IDを設定
          const currentCompanyId = user?.companyId || 'e32a0499-6456-44ac-9b16-b0001098c259'; // デモ会社ID
          demoResetService.setCompanyId(currentCompanyId);

          // プログレス表示用のコールバック
          const onProgress = (message: string, step: number, totalSteps: number) => {
            logger.debug(`[${step}/${totalSteps}] ${message}`);
          };

          const onError = (error: Error | unknown) => {
            logger.error('❌ デモリセットエラー:', { error });
          };

          // スナップショット復元を実行
          const resetResult = await demoResetService.resetDemoData({
            onProgress,
            onError,
            createBackup: true, // リセット前にバックアップを作成
          });

          if (resetResult.success) {
            logger.debug('✅ スナップショット復元完了:', resetResult);

            // データ再取得
            await refreshData();

            // デモデータサービスでリセット状態を記録
            const { demoDataService } = await import('../services/demoDataService');
            demoDataService.markDataReset();

            logger.debug('✅ デモデータリセット完了 - スナップショット方式');
          } else {
            // スナップショット復元が失敗した場合は従来方式にフォールバック
            logger.warn(
              '⚠️ スナップショット復元失敗、従来方式にフォールバック:',
              resetResult.message
            );

            CacheManager.invalidate('supabaseService');
            const supabaseService = getSupabaseServiceInstance();
            await supabaseService.resetDemoData();
            await refreshData();

            const { demoDataService } = await import('../services/demoDataService');
            demoDataService.markDataReset();

            logger.debug('✅ デモデータリセット完了 - フォールバック方式');
          }
        } catch (error) {
          logger.error('❌ デモデータリセット失敗:', { error });

          // フォールバック: 従来のsupabaseService.resetDemoData()を使用
          logger.debug('⚠️ フォールバック: 従来のリセット方式を使用');
          try {
            CacheManager.invalidate('supabaseService');
            const supabaseService = getSupabaseServiceInstance();
            await supabaseService.resetDemoData();
            await refreshData();

            const { demoDataService } = await import('../services/demoDataService');
            demoDataService.markDataReset();

            logger.debug('✅ デモデータリセット完了 - フォールバック方式');
          } catch (fallbackError) {
            logger.error('❌ フォールバックも失敗:', fallbackError);
            // 最終的にローカル状態のみクリア
            clearLocalState();
          }
        }
      } else {
        // 本番モードまたは非デモ環境では従来通りローカル状態のみクリア
        logger.debug('🏢 本番モード: ローカル状態のみクリア');
        clearLocalState();
      }
    } catch (error) {
      logger.error('データリセットエラー:', { error });
      // エラー時もローカル状態はクリア
      clearLocalState();
    } finally {
      setLoading(false);
    }
  }, [refreshData, user?.companyId]);

  // ローカル状態クリア用のヘルパー関数
  const clearLocalState = useCallback(() => {
    setClients([]);
    setLandTransporters([]);
    setCases([]);
    setVehicleModels([]);
    setVehicleTypes([]);
    setManufacturers([]);
    setBusinessPartners([]);
    setCompanies([]);
    setCompanyTimeSettings([]);
    setCompanyOCRSettings([]);
    setCompanyPartnerSettings([]);
    setCompanyVehicleModelSettings([]);
    setCompanyTransportFeeSettings([]);
  }, []);

  // 初期化（ユーザー認証後に実行）
  useEffect(() => {
    if (user) {
      logger.debug('🚀 MasterDataContext: 認証済み - データ取得開始');
      refreshData();
    } else {
      logger.debug('⏸️ MasterDataContext: 認証待ち');
    }
  }, [user, refreshData]); // userまたはrefreshDataが変更されたときに実行

  // 車両モデル関連のヘルパー関数（自己参照を避けるため先に定義）
  const getFilteredVehicleModels = (companyId?: string): VehicleModel[] => {
    logger.debug('🔧 getFilteredVehicleModels呼び出し', {
      companyId,
      totalVehicleModels: vehicleModels.length,
      companyVehicleModelSettingsCount: companyVehicleModelSettings.length,
    });

    if (!companyId) {
      logger.debug('🔧 companyId未指定 - 全車両モデルを返す');
      return vehicleModels;
    }

    const settings = companyVehicleModelSettings.find(s => s.companyId === companyId);
    if (!settings) {
      logger.debug('🔧 設定が見つからない - 全車両モデルを返す', { companyId });
      return vehicleModels;
    }

    logger.debug('🔧 設定発見', {
      companyId: settings.companyId,
      allowRakuhaiProvided: settings.dataSourcePreferences.allowRakuhaiProvided,
      enabledModelIdsCount: settings.enabledModelIds?.length || 0,
    });

    // 有効なモデルIDをSetに変換（パフォーマンス最適化）
    const enabledModelIds = new Set(settings.enabledModelIds || []);

    const filteredModels = vehicleModels.filter(model => {
      // 取引先と同じパターン: データソース別フィルタリング
      if (model.dataSource === VehicleModelDataSource.RAKUHAI_PROVIDED) {
        const isEnabled = enabledModelIds.has(model.id);
        logger.debug('🔧 ラクハイ提供モデル判定', {
          modelId: model.id,
          modelName: model.name,
          isEnabled,
        });
        // ラクハイ提供データは設定で有効化されたもののみ表示
        return isEnabled;
      }

      // 独自追加データ（CUSTOM）は常に表示
      // 設定に関係なく全て表示される
      logger.debug('🔧 カスタムモデル - 常に表示', {
        modelId: model.id,
        modelName: model.name,
        dataSource: model.dataSource,
      });
      return true;
    });

    logger.debug('🔧 フィルタリング結果', {
      originalCount: vehicleModels.length,
      filteredCount: filteredModels.length,
      filteredModels: filteredModels
        .slice(0, 5)
        .map(m => ({ id: m.id, name: m.name, dataSource: m.dataSource })),
    });

    return filteredModels;
  };

  // ラクハイ管理者専用：すべての車両モデルを取得（会社フィルタリング無視）
  const getAllVehicleModelsForAdmin = (): VehicleModel[] => {
    logger.debug('🔧 getAllVehicleModelsForAdmin呼び出し - ラクハイ管理者用');
    logger.debug(`🔧 全車両モデル件数: ${vehicleModels.length}`);

    // SUPER_ADMINは会社フィルタリングを無視して全車両モデルを取得
    return vehicleModels;
  };

  // ラクハイ管理者専用：すべての車種を取得（会社フィルタリング無視）
  const getAllVehicleTypesForAdmin = (): VehicleType[] => {
    logger.debug('🔧 getAllVehicleTypesForAdmin呼び出し - ラクハイ管理者用');
    logger.debug(`🔧 全車種件数: ${vehicleTypes.length}`);

    // SUPER_ADMINは会社フィルタリングを無視して全車種を取得
    return vehicleTypes;
  };

  const getAvailableVehicleModels = (companyId?: string): VehicleModel[] => {
    const filteredModels = getFilteredVehicleModels(companyId);

    // 基本的な可用性チェック（将来的に拡張可能）
    return filteredModels.filter(model => {
      // アクティブなモデルのみ
      return model.id && model.name;
    });
  };

  // コンテキスト値
  const contextValue: MasterDataContextType = {
    // データ
    clients,
    landTransporters,
    cases,
    vehicleModels,
    vehicleTypes,
    manufacturers,
    companyVehicles,
    businessPartners,
    companies,
    companyTimeSettings,
    companyOCRSettings,
    companyDeadlineRuleSettings,
    companyPartnerSettings,
    companyVehicleModelSettings,
    companyTransportFeeSettings,
    deadlineRules,
    loading,

    // CRUD操作（簡略化実装）
    addClient: async client => {
      try {
        logger.debug('🔄 新規取引先追加開始:', client.companyName);

        // デバッグ: Supabase接続状態確認
        const { debugSupabaseConnection, debugAuthAndRLS } = await import('../utils/supabase');

        logger.debug('🔧 デバッグ情報を出力中...');
        debugSupabaseConnection();
        await debugAuthAndRLS();

        // ClientCompatからBusinessPartnerへの変換
        const businessPartnerData: Omit<BusinessPartner, 'id'> = {
          name: client.companyName,
          displayName: client.companyName,
          type: client.type || BusinessPartnerType.CORPORATE,
          status: BusinessPartnerStatus.ACTIVE,
          dataSource: BusinessPartnerDataSource.CUSTOM, // 独自追加データとして登録
          category: client.category || BusinessPartnerCategory.DEALERSHIP,
          parentId: undefined,
          availableRoles: client.availableRoles || [
            BusinessPartnerRole.BILLING,
            BusinessPartnerRole.PICKUP,
            BusinessPartnerRole.DELIVERY,
          ],
          defaultAddress: client.address,
          defaultPhone: client.phone,
          defaultContactPerson: client.contactPerson,
          defaultEmail: client.email,
          notes: client.notes || '',
          isSystemPreset: false,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          locations: client.address
            ? [
                {
                  id: crypto.randomUUID(),
                  name: '本社',
                  address: client.address,
                  phone: client.phone,
                  contactPerson: client.contactPerson,
                  email: client.email,
                  isDefault: true,
                  roles: client.availableRoles || [
                    BusinessPartnerRole.BILLING,
                    BusinessPartnerRole.PICKUP,
                    BusinessPartnerRole.DELIVERY,
                  ],
                },
              ]
            : [],
        };

        logger.debug('📋 作成予定の取引先データ:', {
          name: businessPartnerData.name,
          dataSource: businessPartnerData.dataSource,
          type: businessPartnerData.type,
          status: businessPartnerData.status,
        });

        // BusinessPartnerを作成
        logger.debug('💾 createBusinessPartner実行中...');
        CacheManager.invalidate('supabaseService');
        const dataService = getSupabaseServiceInstance();
        await dataService.createBusinessPartner(businessPartnerData);

        // データを再読み込み
        logger.debug('🔄 データ再読み込み中...');
        await refreshData();

        logger.debug('✅ 新規取引先登録完了:', client.companyName);
      } catch (error) {
        logger.error('❌ 新規取引先登録エラー:', { error });

        // デバッグ: エラー詳細を出力
        if (error && typeof error === 'object') {
          logger.error('📊 エラー詳細情報:');
          logger.error('- type:', typeof error);
          logger.error('- constructor:', error.constructor?.name);
          logger.error('- message:', (error as any).message);
          logger.error('- code:', (error as any).code);
          logger.error('- details:', (error as any).details);
          logger.error('- hint:', (error as any).hint);
          logger.error('- stack:', (error as any).stack);
        }

        // ユーザーにわかりやすいエラー表示
        if (error instanceof Error) {
          if (
            error.message.includes('認証が必要') ||
            error.message.includes('AUTHENTICATION_ERROR')
          ) {
            alert('認証エラー: ログインし直してください。');
          } else if (
            error.message.includes('権限がありません') ||
            error.message.includes('PERMISSION_DENIED')
          ) {
            alert(
              '権限エラー: データベースへの書き込み権限がありません。管理者にお問い合わせください。'
            );
          } else if (error.message.includes('Supabaseへの接続')) {
            alert('データベース接続エラーが発生しました。システム管理者にお問い合わせください。');
          } else if (error.message.includes('ネットワーク') || error.message.includes('Network')) {
            alert(
              'ネットワークエラーが発生しました。インターネット接続を確認して再度お試しください。'
            );
          } else {
            alert(`取引先の保存に失敗しました: ${error.message}`);
          }
        } else {
          alert('取引先の保存に失敗しました。もう一度お試しください。');
        }

        throw error;
      }
    },
    updateClient: async (id, client) => {
      try {
        const dataService = getSupabaseServiceInstance();
        // ClientCompatからBusinessPartnerへの変換（部分更新）
        const businessPartnerData: Partial<BusinessPartner> = {
          ...(client.companyName && {
            name: client.companyName,
            displayName: client.companyName,
          }),
          ...(client.type && { type: client.type }),
          ...(client.category && { category: client.category }),
          ...(client.availableRoles && { availableRoles: client.availableRoles }),
          ...(client.address && { defaultAddress: client.address }),
          ...(client.phone && { defaultPhone: client.phone }),
          ...(client.contactPerson && { defaultContactPerson: client.contactPerson }),
          ...(client.email && { defaultEmail: client.email }),
          ...(client.notes !== undefined && { notes: client.notes }),
          ...(client.status && {
            status:
              client.status === 'Active'
                ? BusinessPartnerStatus.ACTIVE
                : BusinessPartnerStatus.INACTIVE,
          }),
        };

        // BusinessPartnerを更新
        await dataService.updateBusinessPartner(id, businessPartnerData);

        // データを再読み込み
        await refreshData();

        logger.debug('✅ 取引先更新完了:', client.companyName || id);
      } catch (error) {
        logger.error('❌ 取引先更新エラー:', { error });
        throw error;
      }
    },
    removeClient: async id => {
      const dataService = getSupabaseServiceInstance();
      try {
        // BusinessPartnerを削除
        await dataService.deleteBusinessPartner(id);

        // データを再読み込み
        await refreshData();

        logger.debug('✅ 取引先削除完了:', id);
      } catch (error) {
        logger.error('❌ 取引先削除エラー:', { error });
        throw error;
      }
    },
    addLandTransporter: async _transporter => {
      /* 実装 */
    },
    updateLandTransporter: async (id, transporter) => {
      try {
        logger.debug('🔄 陸送員更新開始:', { id, name: transporter.name });

        // supabaseVehicleServiceを使用してデータベースを更新
        const vehicleService = new SupabaseVehicleService();
        const updatedTransporter = await vehicleService.updateLandTransporter(id, transporter);

        // ローカル状態を更新
        setLandTransporters(prev => prev.map(t => (t.id === id ? updatedTransporter : t)));

        logger.debug('✅ 陸送員更新完了:', updatedTransporter.name);
      } catch (error) {
        logger.error('❌ 陸送員更新エラー:', { error });
        throw error;
      }
    },
    removeLandTransporter: async _id => {
      /* 実装 */
    },
    addCase: async case_ => {
      const dataService = getSupabaseServiceInstance();
      try {
        logger.debug('🔄 新規案件追加開始');

        // FL-113: 車検関連フィールドを含む案件を作成
        const createdCase = await dataService.createCase(case_);

        // ローカル状態を更新
        setCases(prev => [...prev, createdCase]);

        logger.debug('✅ 新規案件登録完了:', createdCase.id);
      } catch (error) {
        logger.error('❌ 新規案件登録エラー:', { error });

        // エラーハンドリング
        if (error instanceof Error) {
          if (error.message.includes('権限') || error.message.includes('permission')) {
            alert(
              '権限エラー: データベースへの書き込み権限がありません。管理者にお問い合わせください。'
            );
          } else if (error.message.includes('Supabaseへの接続')) {
            alert('データベース接続エラーが発生しました。システム管理者にお問い合わせください。');
          } else if (error.message.includes('ネットワーク') || error.message.includes('Network')) {
            alert(
              'ネットワークエラーが発生しました。インターネット接続を確認して再度お試しください。'
            );
          } else {
            alert(`案件の保存に失敗しました: ${error.message}`);
          }
        } else {
          alert('案件の保存に失敗しました。もう一度お試しください。');
        }

        throw error;
      }
    },
    updateCase: async (id, case_) => {
      const dataService = getSupabaseServiceInstance();
      try {
        logger.debug('🔄 案件更新開始:', id);
        logger.info(`[TRANSPORTER-DEBUG] 🔧 MasterDataContext.updateCase開始`, {
          caseId: id,
          hasAssignedTransporterId: 'assignedLandTransporterId' in case_,
          assignedLandTransporterId: case_.assignedLandTransporterId,
          type: typeof case_.assignedLandTransporterId,
        });

        // FL-113: 車検関連フィールドを含む案件を更新
        const updatedCase = await dataService.updateCase(id, case_);

        logger.info(`[TRANSPORTER-DEBUG] 📥 MasterDataContext: DB更新後`, {
          caseId: id,
          updatedAssignedLandTransporterId: updatedCase.assignedLandTransporterId,
          type: typeof updatedCase.assignedLandTransporterId,
        });

        // ローカル状態を更新
        setCases(prev => prev.map(c => (c.id === id ? updatedCase : c)));

        logger.debug('✅ 案件更新完了:', id);
        logger.info(`[TRANSPORTER-DEBUG] ✅ MasterDataContext.updateCase完了`, {
          caseId: id,
          finalAssignedLandTransporterId: updatedCase.assignedLandTransporterId,
        });
      } catch (error) {
        logger.error('❌ 案件更新エラー:', { error });
        logger.info(`[TRANSPORTER-DEBUG] ❌ MasterDataContext.updateCase エラー`, {
          caseId: id,
          error: error instanceof Error ? error.message : String(error),
          errorType: error?.constructor?.name,
        });

        // エラーハンドリング
        if (error instanceof Error) {
          if (error.message.includes('権限') || error.message.includes('permission')) {
            alert(
              '権限エラー: データベースへの更新権限がありません。管理者にお問い合わせください。'
            );
          } else if (error.message.includes('Supabaseへの接続')) {
            alert('データベース接続エラーが発生しました。システム管理者にお問い合わせください。');
          } else if (error.message.includes('ネットワーク') || error.message.includes('Network')) {
            alert(
              'ネットワークエラーが発生しました。インターネット接続を確認して再度お試しください。'
            );
          } else {
            alert(`案件の更新に失敗しました: ${error.message}`);
          }
        } else {
          alert('案件の更新に失敗しました。もう一度お試しください。');
        }

        throw error;
      }
    },
    removeCase: async _id => {
      /* 実装 */
    },
    addVehicleModel: async model => {
      const dataService = getSupabaseServiceInstance();
      try {
        logger.debug('🔄 新規車両モデル追加開始:', model.name);

        // SupabaseServiceのcreateVehicleModelメソッドを使用
        const createdModel = await dataService.createVehicleModel(model);

        // ローカル状態を更新
        setVehicleModels(prev => [...prev, createdModel]);

        logger.debug('✅ 新規車両モデル登録完了:', model.name);
      } catch (error) {
        logger.error('❌ 新規車両モデル登録エラー:', { error });
        throw error;
      }
    },
    addVehicleModelWithDetails: async (_modelName, _manufacturerName) => {
      /* 実装 */ return {} as VehicleModel;
    },
    updateVehicleModel: async (id, model) => {
      const dataService = getSupabaseServiceInstance();
      try {
        logger.debug('🔄 車両モデル更新開始:', id);

        // SupabaseServiceのupdateVehicleModelメソッドを使用
        const updatedModel = await dataService.updateVehicleModel(id, model);

        // ローカル状態を更新
        setVehicleModels(prev => prev.map(vm => (vm.id === id ? updatedModel : vm)));

        logger.debug('✅ 車両モデル更新完了:', id);
      } catch (error) {
        logger.error('❌ 車両モデル更新エラー:', { error });
        throw error;
      }
    },
    deleteVehicleModel: async id => {
      const dataService = getSupabaseServiceInstance();
      try {
        logger.debug('🔄 車両モデル削除開始:', id);

        // SupabaseServiceのdeleteVehicleModelメソッドを使用
        await dataService.deleteVehicleModel(id);

        // ローカル状態を更新
        setVehicleModels(prev => prev.filter(vm => vm.id !== id));

        logger.debug('✅ 車両モデル削除完了:', id);
      } catch (error) {
        logger.error('❌ 車両モデル削除エラー:', { error });
        throw error;
      }
    },
    deleteCase: async (id: string): Promise<void> => {
      const dataService = getSupabaseServiceInstance();
      try {
        logger.debug('🔄 案件削除開始:', id);

        // SupabaseCaseServiceのdeleteCaseメソッドを使用
        await dataService.deleteCase(id);

        // ローカル状態を更新
        setCases(prev => prev.filter(c => c.id !== id));

        logger.debug('✅ 案件削除完了:', id);
      } catch (error) {
        logger.error('❌ 案件削除エラー:', { error });
        throw error;
      }
    },
    addManufacturer: async _manufacturer => {
      /* 実装 */
    },
    updateManufacturer: async (_id, _manufacturer) => {
      /* 実装 */
    },
    deleteManufacturer: async _id => {
      /* 実装 */
    },
    addVehicleType: async type => {
      try {
        logger.debug('🔄 新規車種タイプ追加開始:', type.name);

        // VehicleTypeServiceのcreateメソッドを使用
        const createdType = await VehicleTypeService.create(type);

        // ローカル状態を更新
        setVehicleTypes(prev => [...prev, createdType]);

        logger.debug('✅ 新規車種タイプ登録完了:', type.name);
        return createdType;
      } catch (error) {
        logger.error('❌ 新規車種タイプ登録エラー:', { error });
        throw error;
      }
    },
    updateVehicleType: async (id, type) => {
      try {
        // VehicleTypeServiceのupdateメソッドを使用
        const updatedType = await VehicleTypeService.update(id, type);

        // ローカル状態を更新
        setVehicleTypes(prev => prev.map(t => (t.id === id ? updatedType : t)));

        logger.debug('✅ 車種タイプ更新完了:', updatedType.name);
      } catch (error) {
        logger.error('❌ 車種タイプ更新エラー:', { error });
        throw error;
      }
    },
    deleteVehicleType: async id => {
      try {
        // VehicleTypeServiceのdeleteメソッドを使用
        await VehicleTypeService.delete(id);

        // ローカル状態を更新
        setVehicleTypes(prev => prev.filter(t => t.id !== id));

        logger.debug('✅ 車種タイプ削除完了:', id);
      } catch (error) {
        logger.error('❌ 車種タイプ削除エラー:', { error });
        throw error;
      }
    },

    // FL-28: BusinessPartner CRUD操作
    addBusinessPartner: async partner => {
      const dataService = getSupabaseServiceInstance();
      try {
        logger.debug('🔄 新規BusinessPartner追加開始:', partner.name);

        // BusinessPartnerを作成
        const createdPartner = await dataService.createBusinessPartner(partner);

        // ローカル状態を更新
        setBusinessPartners(prev => [...prev, createdPartner]);

        logger.debug('✅ 新規BusinessPartner登録完了:', partner.name);
      } catch (error) {
        logger.error('❌ 新規BusinessPartner登録エラー:', { error });
        throw error;
      }
    },
    updateBusinessPartner: async (id, partner) => {
      const dataService = getSupabaseServiceInstance();
      try {
        logger.debug('🔄 BusinessPartner更新開始:', id);

        // BusinessPartnerを更新
        const updatedPartner = await dataService.updateBusinessPartner(id, partner);

        // ローカル状態を更新
        setBusinessPartners(prev => prev.map(p => (p.id === id ? updatedPartner : p)));

        logger.debug('✅ BusinessPartner更新完了:', id);
      } catch (error) {
        logger.error('❌ BusinessPartner更新エラー:', { error });
        throw error;
      }
    },
    removeBusinessPartner: async _id => {
      /* 実装 */
    },
    addBusinessPartnerLocation: async (_partnerId, _location) => {
      /* 実装 */
    },
    updateBusinessPartnerLocation: async (_partnerId, _locationId, _location) => {
      /* 実装 */
    },
    removeBusinessPartnerLocation: async (_partnerId, _locationId) => {
      /* 実装 */
    },

    // FL-59: 陸送会社時間設定CRUD操作
    addCompanyTimeSettings: async _settings => {
      /* 実装 */
    },
    updateCompanyTimeSettings: async (_id, _settings) => {
      /* 実装 */
    },
    removeCompanyTimeSettings: async _id => {
      /* 実装 */
    },
    getCompanyTimeSettingsByCompanyId: companyId =>
      companyTimeSettings.find(s => s.companyId === companyId) || null,

    // 陸送会社OCR設定CRUD操作
    addCompanyOCRSettings: async _settings => {
      /* 実装 */
    },
    updateCompanyOCRSettings: async (_id, _settings) => {
      /* 実装 */
    },
    removeCompanyOCRSettings: async _id => {
      /* 実装 */
    },
    getCompanyOCRSettingsByCompanyId: companyId =>
      companyOCRSettings.find(s => s.companyId === companyId) || null,
    initializeCompanyOCRSettings: async _companyId => {
      /* 実装 */
    },

    // 締切ルール設定CRUD操作
    addCompanyDeadlineRuleSettings: async settings => {
      try {
        const newSettings = {
          ...settings,
          id: `deadline_rule_settings_${Date.now()}`,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        setCompanyDeadlineRuleSettings(prev => [...prev, newSettings]);
        logger.info('締切ルール設定を追加しました:', newSettings);
        // TODO: 実際のデータベース保存処理（Supabase等）
      } catch (error) {
        logger.error('締切ルール設定の追加に失敗しました:', error);
        throw error;
      }
    },
    updateCompanyDeadlineRuleSettings: async (id, updates) => {
      try {
        const updatedSettings = {
          ...updates,
          updatedAt: new Date().toISOString(),
        };
        setCompanyDeadlineRuleSettings(prev =>
          prev.map(settings =>
            settings.id === id ? { ...settings, ...updatedSettings } : settings
          )
        );
        logger.info('締切ルール設定を更新しました:', { id, updates: updatedSettings });
        // TODO: 実際のデータベース更新処理（Supabase等）
      } catch (error) {
        logger.error('締切ルール設定の更新に失敗しました:', error);
        throw error;
      }
    },
    removeCompanyDeadlineRuleSettings: async id => {
      try {
        setCompanyDeadlineRuleSettings(prev => prev.filter(settings => settings.id !== id));
        logger.info('締切ルール設定を削除しました:', { id });
        // TODO: 実際のデータベース削除処理（Supabase等）
      } catch (error) {
        logger.error('締切ルール設定の削除に失敗しました:', error);
        throw error;
      }
    },
    getCompanyDeadlineRuleSettingsByCompanyId: companyId =>
      companyDeadlineRuleSettings.find(s => s.companyId === companyId) || null,
    initializeCompanyDeadlineRuleSettings: async _companyId => {
      /* 実装 */
    },

    // 陸送会社取引先表示設定CRUD操作
    addCompanyPartnerSettings: async settings => {
      const dataService = getSupabaseServiceInstance();
      try {
        const newSettings = await dataService.createCompanyPartnerSettings(settings);
        setCompanyPartnerSettings(prev => [...prev, newSettings]);
        logger.debug('✅ 取引先設定を追加しました:', newSettings);
      } catch (error) {
        logger.error('❌ 取引先設定の追加に失敗しました:', { error });
        throw error;
      }
    },
    updateCompanyPartnerSettings: async (id, settings) => {
      const dataService = getSupabaseServiceInstance();
      try {
        const updatedSettings = await dataService.updateCompanyPartnerSettings(id, settings);
        setCompanyPartnerSettings(prev => prev.map(s => (s.id === id ? updatedSettings : s)));
        logger.debug('✅ 取引先設定を更新しました:', updatedSettings);

        // データ再読み込みで表示を最新化
        await refreshData();
      } catch (error) {
        logger.error('❌ 取引先設定の更新に失敗しました:', { error });
        throw error;
      }
    },
    removeCompanyPartnerSettings: async _id => {
      /* 実装 */
    },
    getCompanyPartnerSettingsByCompanyId: companyId =>
      companyPartnerSettings.find(s => s.companyId === companyId) || null,
    initializeCompanyPartnerSettings: async _companyId => {
      /* 実装 */
    },

    // 陸送会社車両モデル選択設定CRUD操作
    addCompanyVehicleModelSettings: async settings => {
      const dataService = getSupabaseServiceInstance();
      try {
        logger.debug('🔄 車両モデル設定追加開始:', settings);
        const newSettings = await dataService.createCompanyVehicleModelSettings(settings);
        setCompanyVehicleModelSettings(prev => [...prev, newSettings]);
        logger.debug('✅ 車両モデル設定を追加しました:', newSettings);
      } catch (error) {
        logger.error('❌ 車両モデル設定の追加に失敗しました:', { error });
        throw error;
      }
    },
    updateCompanyVehicleModelSettings: async (id, settings) => {
      const dataService = getSupabaseServiceInstance();
      try {
        logger.debug('🔄 車両モデル設定更新開始:', { id, settings });
        const updatedSettings = await dataService.updateCompanyVehicleModelSettings(id, settings);
        setCompanyVehicleModelSettings(prev => prev.map(s => (s.id === id ? updatedSettings : s)));
        logger.debug('✅ 車両モデル設定を更新しました:', updatedSettings);

        // データ再読み込みで表示を最新化
        await refreshData();
      } catch (error) {
        logger.error('❌ 車両モデル設定の更新に失敗しました:', { error });
        throw error;
      }
    },
    removeCompanyVehicleModelSettings: async id => {
      const dataService = getSupabaseServiceInstance();
      try {
        logger.debug('🔄 車両モデル設定削除開始:', id);
        await dataService.deleteCompanyVehicleModelSettings(id);
        setCompanyVehicleModelSettings(prev => prev.filter(s => s.id !== id));
        logger.debug('✅ 車両モデル設定を削除しました:', id);
      } catch (error) {
        logger.error('❌ 車両モデル設定の削除に失敗しました:', { error });
        throw error;
      }
    },
    getCompanyVehicleModelSettingsByCompanyId: companyId =>
      companyVehicleModelSettings.find(s => s.companyId === companyId) || null,
    initializeCompanyVehicleModelSettings: async companyId => {
      try {
        logger.debug('🔄 車両モデル設定初期化開始:', companyId);
        const existingSettings = companyVehicleModelSettings.find(s => s.companyId === companyId);

        if (!existingSettings) {
          const { DEFAULT_COMPANY_VEHICLE_MODEL_SETTINGS } = await import('../types');
          const newSettings: CompanyVehicleModelSettings = {
            id: crypto.randomUUID(),
            companyId,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            ...DEFAULT_COMPANY_VEHICLE_MODEL_SETTINGS,
          };

          const dataServiceForCreate = getSupabaseServiceInstance();
          await dataServiceForCreate.createCompanyVehicleModelSettings(newSettings);
          setCompanyVehicleModelSettings(prev => [...prev, newSettings]);
          logger.debug('✅ 車両モデル設定を初期化しました:', newSettings);
        }
      } catch (error) {
        logger.error('❌ 車両モデル設定の初期化に失敗しました:', { error });
        throw error;
      }
    },

    // 陸送会社取引先選択設定（詳細）CRUD操作
    addCompanyPartnerSelection: async (selection: CompanyPartnerSelection) => {
      const dataService = getSupabaseServiceInstance();
      try {
        await dataService.createCompanyPartnerSelection(selection);
        await refreshData(); // データを再取得して状態を更新
      } catch (error) {
        logger.error('CompanyPartnerSelection追加エラー:', { error });
        throw error;
      }
    },
    updateCompanyPartnerSelection: async (
      id: string,
      selection: Partial<CompanyPartnerSelection>
    ) => {
      const dataService = getSupabaseServiceInstance();
      try {
        await dataService.updateCompanyPartnerSelection(id, selection);
        await refreshData(); // データを再取得して状態を更新
      } catch (error) {
        logger.error('CompanyPartnerSelection更新エラー:', { error });
        throw error;
      }
    },
    removeCompanyPartnerSelection: async (id: string) => {
      const dataService = getSupabaseServiceInstance();
      try {
        await dataService.removeCompanyPartnerSelection(id);
        await refreshData(); // データを再取得して状態を更新
      } catch (error) {
        logger.error('CompanyPartnerSelection削除エラー:', { error });
        throw error;
      }
    },
    getCompanyPartnerSelectionsByCompanyId: (companyId: string) => {
      return companyPartnerSelections.filter(selection => selection.companyId === companyId);
    },
    getCompanyPartnerSelectionById: (id: string) => {
      return companyPartnerSelections.find(selection => selection.id === id) || null;
    },
    enableCompanyPartnerSelection: async (id: string) => {
      const dataService = getSupabaseServiceInstance();
      try {
        await dataService.updateCompanyPartnerSelection(id, { isEnabled: true });
        await refreshData();
      } catch (error) {
        logger.error('CompanyPartnerSelection有効化エラー:', { error });
        throw error;
      }
    },
    disableCompanyPartnerSelection: async (id: string) => {
      const dataService = getSupabaseServiceInstance();
      try {
        await dataService.updateCompanyPartnerSelection(id, { isEnabled: false });
        await refreshData();
      } catch (error) {
        logger.error('CompanyPartnerSelection無効化エラー:', { error });
        throw error;
      }
    },
    updateCompanyPartnerUsage: async (id: string, usageCount: number, lastUsedAt?: Date) => {
      const dataService = getSupabaseServiceInstance();
      try {
        const updateData: Partial<CompanyPartnerSelection> = {
          usageCount,
          ...(lastUsedAt && { lastUsedAt: lastUsedAt.toISOString() }),
        };
        await dataService.updateCompanyPartnerSelection(id, updateData);
        // 使用実績更新では全データリフレッシュは不要
        // await refreshData();
      } catch (error) {
        logger.error('CompanyPartnerSelection使用実績更新エラー:', { error });
        throw error;
      }
    },

    // FL-116: 陸送費設定
    getCompanyTransportFeeSettingsByCompanyId: (_companyId: string) => {
      try {
        // デフォルト値を返す（実際の取得はSettingsPageで行う）
        const defaultSettings: TransportFeeSettings = {
          calculation_method: 'fixed',
          fixed_fee: DEFAULT_TRANSPORT_FEE,
        };

        return defaultSettings;
      } catch (error) {
        logger.error('陸送費設定取得エラー:', { error });
        return null;
      }
    },
    updateCompanyTransportFeeSettings: async (companyId, settings) => {
      try {
        const { getSupabaseClient } = await import('../utils/supabase');
        const supabase = getSupabaseClient();
        if (!supabase) {
          throw new Error('Supabaseクライアントが初期化されていません');
        }

        // PostgreSQLのJSONB関数を使用してupsert操作を実行
        const { error } = await supabase.rpc('upsert_transport_fee_settings', {
          p_company_id: companyId,
          p_settings: settings,
        });

        if (error) {
          // RPC関数が存在しない場合は従来のfetch-then-update方式にフォールバック
          logger.warn('upsert RPCが利用できません。従来方式で更新します:', error.message);

          // 現在の設定を取得
          const { data: company, error: fetchError } = await supabase
            .from('companies')
            .select('settings')
            .eq('id', companyId)
            .single();

          if (fetchError) {
            throw new Error(`会社情報の取得に失敗しました: ${fetchError.message}`);
          }

          // 現在の設定と新しい設定をマージ
          const currentSettings = company?.settings || {};
          const updatedSettings = {
            ...currentSettings,
            transport_fee_settings: {
              ...currentSettings.transport_fee_settings,
              ...settings,
            },
          };

          // companiesテーブルのsettingsを更新
          const { error: updateError } = await supabase
            .from('companies')
            .update({
              settings: updatedSettings,
              updated_at: new Date().toISOString(),
            })
            .eq('id', companyId);

          if (updateError) {
            throw new Error(`陸送費設定の更新に失敗しました: ${updateError.message}`);
          }
        }

        logger.debug('✅ 陸送費設定更新完了:', { companyId, settings });
      } catch (error) {
        logger.error('❌ 陸送費設定更新エラー:', { error });
        throw error;
      }
    },

    // ヘルパー関数
    getClientById: id => clients.find(c => c.id === id) || null,
    getLandTransporterById: id => landTransporters.find(t => t.id === id) || null,
    getCaseById: id => cases.find(c => c.id === id) || null,
    getVehicleModelById: id => {
      const result = vehicleModels.find(m => m.id === id) || undefined;
      logger.info('🔍 getVehicleModelById呼び出し - 詳細診断', {
        searchId: id,
        searchIdType: typeof id,
        vehicleModelsCount: vehicleModels.length,
        allVehicleModelIds: vehicleModels.map(m => m.id),
        found: !!result,
        result: result ? { id: result.id, name: result.name } : null,
      });
      return result;
    },
    getVehicleTypeById: id => vehicleTypes.find(t => t.id === id) || null,
    getManufacturerById: id => manufacturers.find(m => m.id === id) || undefined,

    getCompanyVehicleById: (id: string) => companyVehicles.find(v => v.id === id),
    getBusinessPartnerById: id => businessPartners.find(p => p.id === id) || undefined,
    getBusinessPartnerLocationById: (partnerId, locationId) => {
      const partner = businessPartners.find(p => p.id === partnerId);
      return partner?.locations?.find(l => l.id === locationId) || undefined;
    },

    // 車両モデルフィルタリング機能（取引先と同じ仕様）
    getFilteredVehicleModels,
    getAllVehicleModelsForAdmin,
    getAllVehicleTypesForAdmin,
    getAvailableVehicleModels,

    // 車両モデル使用状況カウント機能
    getVehicleModelActiveUsageCount: (vehicleModelId: string): number => {
      // 稼働中案件（unassigned, Assigned, in_transit）で使用されている数をカウント
      return cases.filter(
        c => c.vehicleModel === vehicleModelId && c.status !== 'completed' // completed以外は稼働中とみなす
      ).length;
    },

    // 国産車判定ヘルパー関数
    isDomesticModel: (model: VehicleModel): boolean => {
      const manufacturer = manufacturers.find(m => m.id === model.manufacturerId);
      return manufacturer?.country === '日本';
    },

    // その他
    refreshData,
    refreshBusinessPartners,
    refreshCompanies,
    refreshVehicleTypes,
    refreshLandTransporters,
    getAllBusinessPartners: async () => {
      // システム設定用：全取引先を取得（会社フィルタなし）
      logger.debug('🔍 MasterDataContext: getAllBusinessPartners開始');
      const dataService = getSupabaseServiceInstance();
      try {
        const allPartners = await dataService.getAllBusinessPartners();
        logger.debug(`✅ MasterDataContext: getAllBusinessPartners完了 - ${allPartners.length}件`);
        return allPartners;
      } catch (error) {
        logger.error('❌ MasterDataContext: getAllBusinessPartners失敗:', { error });
        throw error;
      }
    },

    // FL-110: 締切ルールCRUD操作
    addDeadlineRule: async (input: CreateDeadlineRuleInput) => {
      try {
        logger.debug('🔄 新規締切ルール追加開始:', {
          businessPartnerId: input.businessPartnerId,
          ruleType: input.ruleType,
          dayOfWeek: input.dayOfWeek,
        });

        const createdRule = await deadlineRuleService.createDeadlineRule(input);

        // ローカル状態を更新
        setDeadlineRules(prev => [...prev, createdRule]);

        logger.debug('✅ 新規締切ルール登録完了:', createdRule.id);
        return createdRule;
      } catch (error) {
        logger.error('❌ 新規締切ルール登録エラー:', { error });
        throw error;
      }
    },
    updateDeadlineRule: async (id: string, input: UpdateDeadlineRuleInput) => {
      try {
        logger.debug('🔄 締切ルール更新開始:', { id, input });

        const updatedRule = await deadlineRuleService.updateDeadlineRule(id, input);

        // ローカル状態を更新
        setDeadlineRules(prev => prev.map(rule => (rule.id === id ? updatedRule : rule)));

        logger.debug('✅ 締切ルール更新完了:', id);
        return updatedRule;
      } catch (error) {
        logger.error('❌ 締切ルール更新エラー:', { error });
        throw error;
      }
    },
    removeDeadlineRule: async (id: string) => {
      try {
        logger.debug('🔄 締切ルール削除開始:', id);

        await deadlineRuleService.deleteDeadlineRule(id);

        // ローカル状態を更新
        setDeadlineRules(prev => prev.filter(rule => rule.id !== id));

        logger.debug('✅ 締切ルール削除完了:', id);
      } catch (error) {
        logger.error('❌ 締切ルール削除エラー:', { error });
        throw error;
      }
    },
    getDeadlineRulesByBusinessPartnerId: (businessPartnerId: string, companyId: string) => {
      return deadlineRules.filter(
        rule => rule.businessPartnerId === businessPartnerId && rule.companyId === companyId
      );
    },
    refreshDeadlineRules: async () => {
      if (!user?.companyId) {
        logger.warn('refreshDeadlineRules: companyIdが設定されていません');
        return;
      }

      try {
        logger.debug('🔄 締切ルール再取得開始:', user.companyId);
        const refreshedRules = await deadlineRuleService.getAllDeadlineRules(user.companyId);
        setDeadlineRules(refreshedRules);
        logger.debug('✅ 締切ルール再取得完了:', refreshedRules.length);
      } catch (error) {
        logger.error('❌ 締切ルール再取得エラー:', { error });
        throw error;
      }
    },
    resetData,
  };

  // 🔍 診断: vehicleModels状態変化の監視
  useEffect(() => {
    logger.info('🚗 vehicleModels状態変化検知 - 詳細診断', {
      count: vehicleModels.length,
      allIds: vehicleModels.map(vm => vm.id),
      sampleData: vehicleModels.slice(0, 5).map(vm => ({
        id: vm.id,
        name: vm.name,
        manufacturerId: vm.manufacturerId,
        dataSource: vm.dataSource,
      })),
      クラウンUUID検索: vehicleModels.find(vm => vm.id === 'c3d4e5f6-a7b8-5901-cdef-0123456789bc'),
    });
  }, [vehicleModels]);

  return <MasterDataContext.Provider value={contextValue}>{children}</MasterDataContext.Provider>;
};
